import { type FC, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import FontAwesomeLight from "../../../../app/Utilities/Platform/FontAwesome/FontAwesomeLight";
import { type NavigationType } from "./Navigation";
import { NavigationItem } from "./NavigationItem";

interface Props {
    path: Array<string>;
    navigation: NavigationType;
}

export const NavigationMenu: FC<Props> = ({ path, navigation }) => {
    const [ expanded, setExpanded ] = useState<Record<string, boolean>>({
        [path[0]]: true,
    });

    useEffect(() => {
        // Wait for navigation to load
        if(!Object.keys(navigation).length) {
            return;
        }

        setExpanded(prevState => {
            const results: Record<string, boolean> = {};

            for(const key in navigation) {
                if(!Object.prototype.hasOwnProperty.call(navigation, key)) {
                    continue;
                }

                results[key] = prevState[key] ?? false;
            }

            return results;
        });
    }, [ navigation ]);

    return (
        <ul className="list-none space-y-1 p-0 m-0">
            {Object.keys(navigation).map(group => {
                if(group === "_search") {
                    return null;
                }

                const children = navigation[group];

                const groupPath = group.replace(/ /g, "-");

                const inGroup = path[0] === group;

                if(typeof children === "number") {
                    return (
                        <li key={group}>
                            <Link
                                to={`/documentation/${groupPath}`}
                                className="w-full px-8 py-1"
                            >
                                {group}
                            </Link>
                        </li>
                    );
                }

                return (
                    <li key={group} className="px-2">
                        <button
                            className={
                                "text-base text-left border-0 px-12 py-1 box-border " +
                                "w-full cursor-pointer rounded font-bold bg-transparent hover:bg-neutral-300 " + (
                                    inGroup ?
                                        "text-sky-700" :
                                        "text-neutral-800"
                                )
                            }
                            onClick={() => {
                                setExpanded(prevState => ({
                                    ...prevState,
                                    [group]: !(prevState[group] ?? false),
                                }));
                            }}
                        >
                            <FontAwesomeLight
                                className="-ml-7 mr-2 right-full"
                                icon={expanded[group] ? "angle-down" : "angle-right"}
                            />
                            {group}
                        </button>
                        {expanded[group] && (
                            <ul className="list-none space-y-1 p-0 mx-0 mt-1 mb-4 w-full">
                                {Object.keys(children).map(child => (
                                    <NavigationItem
                                        key={`${group}|${child}`}
                                        name={child}
                                        group={groupPath}
                                        isSelected={inGroup && path[1] === child}
                                        item={children[child]}
                                    />
                                ))}
                            </ul>
                        )}
                    </li>
                );
            })}
        </ul>
    );
};
